import { cn } from "@universe/ui/lib/utils";
import { usePathname } from "next/navigation";
import React from "react";

import { Route } from "@/enums/route";

export type GroupedListProps<T extends { id: string } | { uuid: string }> =
  React.HTMLAttributes<HTMLDivElement> & {
    groupedList: [string, T[]][];
    renderGroupTitle?: (groupTitle: string) => React.ReactNode;
    renderGroupItem: (item: T, index: number) => React.ReactNode;
    headerClassName?: string;
    /** Dynamic top position in pixels. When provided, overrides class-based positioning. */
    dateGroupTop?: number;
    /** Per-item reveal delays (ms) for newly loaded rows */
    revealingItemDelays?: ReadonlyMap<string, number>;
  };

const getItemKey = <T extends { id: string } | { uuid: string }>(item: T) =>
  "id" in item ? item.id : item.uuid;

const GroupedList = <T extends { id: string } | { uuid: string }>({
  groupedList,
  renderGroupTitle,
  renderGroupItem,
  className,
  headerClassName,
  dateGroupTop,
  revealingItemDelays,
  ...rest
}: GroupedListProps<T>) => {
  const pathname = usePathname();
  const isDashboardPage = pathname === Route.Dashboard;

  return (
    <div className={cn("min-h-[550px]", className)} {...rest}>
      {groupedList.map(([groupTitle, groupItems]) => (
        <div
          data-testid="grouped-list"
          key={groupTitle}
          className="mb-2 lg:mb-3"
        >
          <div
            className={cn(
              // Solid white. This was six gradient classes whose three stops
              // were all `white/100`, so it always painted flat anyway (the
              // out-of-order 80%/70%/50% positions never had anything to
              // interpolate) — and a backdrop blur behind an opaque fill is
              // invisible work.
              "sticky px-3 lg:px-6 -mx-0 lg:-mx-3 pt-4 lg:pt-6 pb-2 font-medium text-primary z-1 bg-white text-sm md:text-base",
              headerClassName
            )}
            style={{
              top: dateGroupTop
                ? `calc(${dateGroupTop}px + var(--s21-banner-height, 0px))`
                : isDashboardPage
                  ? "calc(220px + var(--s21-banner-height, 0px))"
                  : "calc(210px + var(--s21-banner-height, 0px))",
              transform: "translateY(-1px)",
            }}
          >
            {renderGroupTitle?.(groupTitle) ?? groupTitle}
          </div>
          {groupItems.map((item, index) => {
            const itemKey = getItemKey(item);
            const delayMs = revealingItemDelays?.get(itemKey);

            if (delayMs === undefined) {
              return (
                <React.Fragment key={`${itemKey}-${index}`}>
                  {renderGroupItem(item, index)}
                </React.Fragment>
              );
            }

            return (
              <div
                key={`${itemKey}-${index}`}
                className="ledger-item-reveal"
                style={
                  {
                    "--ledger-item-delay": `${delayMs}ms`,
                  } as React.CSSProperties
                }
              >
                {renderGroupItem(item, index)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default GroupedList;
